import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Select, Table, Typography, Space, Spin, Alert, DatePicker, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

const { Title, Text } = Typography;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtMonth(ym) {
  const [y, m] = ym.split('-');
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y}`;
}

// Colours come from index.css (.upsell-tier-*): the same pastel in both
// themes, always with dark text (in dark mode the text was white on it).
const TIERS = [
  { label: 'No Upsells',  cls: 'upsell-tier-none', test: v => !v },
  { label: '1-3 Upsells', cls: 'upsell-tier-low',  test: v => v >= 1 && v <= 2 },
  { label: '3-6 Upsells', cls: 'upsell-tier-mid',  test: v => v >= 3 && v <= 5 },
  { label: '6+ Upsells',  cls: 'upsell-tier-high', test: v => v >= 6 },
];

function cellClass(v) {
  for (const t of TIERS) if (t.test(v)) return t.cls;
  return undefined;
}

/**
 * Upselling stats, standalone or embedded in the Campaign Summary.
 *
 * Embedded it loses its heading and its own campaign and month pickers, and
 * takes the scope from the page above instead. Every gate is on `embedded`,
 * so the standalone page is untouched by any of it.
 */
export default function ConHomeUpsellStatsPage({
  embedded = false, campaignId: campaignIdProp = null, from = null, to = null,
}) {
  const currentUser = useContext(UserContext);
  const isAuth = !!currentUser;
  const isAgent = currentUser?.Role === 'agent';

  const now = dayjs();
  const [campaigns, setCampaigns]       = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [sortBy, setSortBy] = useState({ field: null, order: null });
  const [campaignId, setCampaignId]     = useState(null);
  const [monthFrom, setMonthFrom]   = useState(dayjs(`${now.year()}-01-01`));
  const [monthTo, setMonthTo]       = useState(dayjs(`${now.year()}-12-01`));
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [months, setMonths]         = useState([]);
  const [rows, setRows]             = useState([]);

  // Only to populate the selector, which embedded mode does not show.
  useEffect(() => {
    if (!isAuth || embedded) return;
    setCampaignsLoading(true);
    const params = {};
    if (isAgent) {
      params.month_from = monthFrom.format('YYYY-MM');
      params.month_to   = monthTo.format('YYYY-MM');
    }
    api.get('/api/conhome-upsell-campaigns', { params })
      .then(r => {
        const list = r.data || [];
        setCampaigns(list);
        setCampaignId(prev => {
          if (list.length === 1) return list[0].ID;
          return list.some(c => c.ID === prev) ? prev : null;
        });
      })
      .catch(() => {})
      .finally(() => setCampaignsLoading(false));
  }, [isAuth, isAgent, embedded, monthFrom, monthTo]);

  // Scope in force: the parent's when embedded, this page's own otherwise.
  // Held as formatted strings rather than dayjs objects because they are
  // fetch dependencies -- a fresh object every render would re-fetch forever.
  const effCampaignId = embedded ? campaignIdProp : campaignId;
  const effMonthFrom  = embedded && from ? dayjs(from).format('YYYY-MM') : monthFrom.format('YYYY-MM');
  const effMonthTo    = embedded && to   ? dayjs(to).format('YYYY-MM')   : monthTo.format('YYYY-MM');

  const fetchStats = useCallback(() => {
    if (!isAuth) return;
    if (embedded && !effCampaignId) { setMonths([]); setRows([]); return; }
    setLoading(true);
    setError(null);
    const params = { month_from: effMonthFrom, month_to: effMonthTo };
    if (effCampaignId) params.campaign_id = effCampaignId;
    api.get('/api/conhome-upsell-stats', { params })
      .then(r => { setMonths(r.data.months || []); setRows(r.data.rows || []); })
      .catch(err => setError(err.response?.data?.error || 'Failed to load stats.'))
      .finally(() => setLoading(false));
  }, [isAuth, embedded, effCampaignId, effMonthFrom, effMonthTo]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // The Total row lives in dataSource, so the Table's own sorters would shuffle
  // it in among the agents. Sorting is controlled here instead: the agent rows
  // are sorted and the total is appended afterwards, staying at the bottom
  // whichever column you sort by.
  const sortedRows = useMemo(() => {
    const { field, order } = sortBy;
    if (!field || !order) return rows;
    const dir = order === 'ascend' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (field === 'agent') return dir * (a.agent || '').localeCompare(b.agent || '');
      // A month with no rows for an agent is a genuine zero for ranking.
      return dir * ((a[field] || 0) - (b[field] || 0))
          || (a.agent || '').localeCompare(b.agent || '');
    });
  }, [rows, sortBy]);

  const handleTableChange = (_pagination, _filters, s) => {
    setSortBy(s?.order ? { field: s.columnKey, order: s.order } : { field: null, order: null });
  };

  if (!isAuth) {
    return <div style={{ padding: 24 }}><Text type="secondary">Access denied.</Text></div>;
  }

  const monthCols = months.map(ym => ({
    title: fmtMonth(ym),
    dataIndex: ym,
    key: ym,
    align: 'center',
    width: 72,
    sorter: true,
    sortOrder: sortBy.field === ym ? sortBy.order : null,
    onCell: row => (row._isTotal ? {} : { className: cellClass(row[ym]), style: { padding: '4px 8px' } }),
    render: v => (v ? v.toLocaleString() : <span style={{ opacity: 0.55 }}>-</span>),
  }));

  const totalRow = months.reduce((acc, ym) => {
    acc[ym] = rows.reduce((s, r) => s + (r[ym] || 0), 0);
    return acc;
  }, { agent: 'Total', total: rows.reduce((s, r) => s + (r.total || 0), 0), _isTotal: true });

  const tableData = [...sortedRows, ...(sortedRows.length ? [totalRow] : [])];

  // Exports exactly what is on screen: same campaign and month range, same sort
  // order, the same month columns, and the Total row last.
  const exportToExcel = () => {
    const line = (r) => {
      const out = { Agent: r.agent };
      months.forEach(ym => { out[fmtMonth(ym)] = r[ym] || 0; });
      out.Total = r.total || 0;
      return out;
    };
    const ws = XLSX.utils.json_to_sheet([...sortedRows.map(line), line(totalRow)]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Upselling Stats');
    const camp = campaigns.find(c => c.ID === effCampaignId)?.Name || 'all-campaigns';
    const span = `${effMonthFrom}-to-${effMonthTo}`;
    XLSX.writeFile(wb, `upsell-stats-${camp}-${span}.xlsx`.replace(/\s+/g, '-'));
  };

  const columns = [
    {
      title: 'Agent',
      dataIndex: 'agent',
      key: 'agent',
      fixed: 'left',
      width: 200,
      sorter: true,
      sortOrder: sortBy.field === 'agent' ? sortBy.order : null,
      render: (v, row) => row._isTotal ? <Text strong>{v}</Text> : v,
    },
    ...monthCols,
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'center',
      width: 72,
      fixed: 'right',
      sorter: true,
      sortOrder: sortBy.field === 'total' ? sortBy.order : null,
      render: (v, row) => <Text strong={row._isTotal}>{v ? v.toLocaleString() : '-'}</Text>,
    },
  ];

  return (
    <div style={{ padding: embedded ? 0 : '24px' }}>
      {!embedded && <Title level={4} style={{ marginTop: 0 }}>Upselling Stats</Title>}

      <Space wrap style={{ marginBottom: 16 }}>
        {/* The campaign and the window come from the page above when embedded,
            so its own pickers would be two sets of controls disagreeing. */}
        {!embedded && (
          <>
            <Select
              placeholder="Campaign"
              value={campaignId}
              onChange={setCampaignId}
              allowClear
              loading={campaignsLoading}
              style={{ minWidth: 200 }}
              options={campaigns.map(c => ({ value: c.ID, label: c.Name }))}
            />
            <DatePicker
              picker="month"
              value={monthFrom}
              onChange={v => v && setMonthFrom(v)}
              format="MMM YYYY"
              allowClear={false}
            />
            <Text type="secondary">to</Text>
            <DatePicker
              picker="month"
              value={monthTo}
              onChange={v => v && setMonthTo(v)}
              format="MMM YYYY"
              allowClear={false}
            />
          </>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={exportToExcel}
          disabled={!rows.length}
        >
          Export to Excel
        </Button>
      </Space>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {TIERS.map(t => (
          <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div className={t.cls} style={{ width: 18, height: 18, border: '1px solid var(--ant-color-border)', borderRadius: 2 }} />
            <Text style={{ fontSize: 12 }}>{t.label}</Text>
          </div>
        ))}
      </div>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />}

      <Spin spinning={loading}>
        <div style={{ overflowX: 'auto' }}>
          <Table
            rowKey={(r, i) => r._isTotal ? '__total__' : r.agent + i}
            size="small"
            pagination={false}
            columns={columns}
            dataSource={tableData}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
            rowClassName={row => row._isTotal ? 'upsell-total-row' : ''}
            locale={{ emptyText: loading ? ' ' : 'No data for the selected range.' }}
          />
        </div>
      </Spin>

      <style>{`
        .upsell-total-row td { background: var(--ant-color-fill-secondary) !important; font-weight: 600; }
      `}</style>
    </div>
  );
}
